import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { forgotPassword, clearError } from '../../store/authSlice';
import { COLORS } from '../../shared/colors';
import { REGEX } from '../../shared/regex';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment
} from '@mui/material';
import { EmailOutlined, ArrowBack } from '@mui/icons-material';
import AuthFormShell from './AuthFormShell';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const validationTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      dispatch(clearError());
    };
  }, [dispatch]);

  const validateEmail = (value) => REGEX.email.test(value);

  const debouncedValidation = useCallback(
    (value) => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      validationTimeoutRef.current = setTimeout(() => {
        if (value && !validateEmail(value)) {
          setEmailError(t('auth.validEmailRequired'));
        } else {
          setEmailError('');
        }
      }, 300);
    },
    [t]
  );

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError('');
    debouncedValidation(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setEmailError(t('auth.validEmailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t('auth.validEmailRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await dispatch(forgotPassword({ email })).unwrap();
      setIsNewUser(response?.is_new_user === true);
      setSuccess(true);
    } catch (err) {
      const errorMessage =
        typeof err === 'string' ? err : err?.message || t('forgotPassword.resetLinkSentError');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    const titleColor = isNewUser ? '#dd2b37' : '#082B72';
    const subtitleColor = isNewUser ? '#082B72' : '#80899A';

    return (
      <AuthFormShell maxWidth={440} minHeight={360} contentTop={72} cardPaddingX={2}>
      <Box sx={{ width: '100%', maxWidth: 440, px: 0 }}>
        <Box sx={{ textAlign: 'center', mb: 2.5, mt: 0.5 }}>
          <Box aria-hidden="true" sx={{ height: 54, mb: 0.5 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: titleColor,
              mb: 1,
              fontSize: { xs: '26px', sm: '30px' },
              letterSpacing: '-0.25px',
            }}
          >
            {isNewUser ? t('forgotPassword.newUserTitle') : t('forgotPassword.resetLinkSent')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: subtitleColor,
              fontWeight: isNewUser ? 600 : 500,
              fontSize: { xs: '16px', sm: '19px' },
              lineHeight: 1.4,
              maxWidth: 300,
              mx: 'auto',
              mb: 1.5
            }}
          >
            {isNewUser ? t('forgotPassword.newUserTagline') : t('forgotPassword.CheckEmailForResetInstructions')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            startIcon={<ArrowBack />}
            sx={{
              borderColor: '#082B72',
              color: '#082B72',
              textTransform: 'none',
              px: 2,
              py: 0.9,
              mt: 0.5,
              gap: 1.25,
              '&:hover': {
                borderColor: '#082B72',
                backgroundColor: 'rgba(8, 43, 114, 0.08)'
              }
            }}
          >
            {t('forgotPassword.backToLogin')}
          </Button>
        </Box>
      </Box>
      </AuthFormShell>
    );
  }

  return (
    <AuthFormShell maxWidth={560} height={error?560:500} contentTop={70} cardPaddingX={6}>
    <Box sx={{ width: '100%', maxWidth: 560, px: 0 }}>
      <Box sx={{ textAlign: 'center', mt: 0.5, mb: 5 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            color: '#082B72',
            mb: 1,
            fontSize: { xs: '30px', sm: '36px' }
          }}
        >
          {t('forgotPassword.title')}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#80899A',
            fontWeight: 400,
            fontSize: { xs: '18px', sm: '24px' }
          }}
        >
          {t('forgotPassword.subtitle')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: '100%' }}>
        <TextField
          fullWidth
          variant="outlined"
          label={t('forgotPassword.enterEmail')}
          type="email"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlined className="input-icon-red" fontSize="small" />
              </InputAdornment>
            )
          }}
          className="red-outlined-input"
          sx={{
            mb: 4,
            '& .MuiFormHelperText-root': {
              color: '#C51925'
            }
          }}
        />

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: COLORS.alert.error
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !!emailError}
            sx={{
              width: '100%',
              height: 52,
              bgcolor: '#082B72',
              borderRadius: '4px',
              fontSize: '20px',
              fontWeight: 400,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#061f53',
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: '#5f76a8',
                color: '#ffffff'
              }
            }}
          >
            {isLoading ? t('forgotPassword.sending') : t('forgotPassword.sendResetLink')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="text"
            onClick={() => navigate('/login')}
            startIcon={<ArrowBack />}
            sx={{
              color: '#80899A',
              textTransform: 'none',
              fontWeight: 400,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#082B72'
              }
            }}
          >
            {t('forgotPassword.backToLogin')}
          </Button>
        </Box>
      </Box>
    </Box>
    </AuthFormShell>
  );
};

export default ForgotPasswordForm;
